import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {RouteProps} from "react-router-dom";
import {MONTHS_ABV, MONTHS_NUMBERS} from "../../../constants/StaffingReportingConstants";
import useLoading from "../../../hooks/useLoading";
import useSearchFilter from "../../../hooks/useSearchFilter";
import {ReportingData} from "../../../interfaces/CalendarInterface";
import {SearchFilter} from "../../../interfaces/api/FiltersInterface";
import {staffingCalendarService} from "../../../services/StaffingCalendarService";
import StaffingReportingFilters from "../filters/staffingcalendar/StaffingReportingFilters";
import CalendarTable, {CalendarTableRef} from "../table/staffingcalendar/CalendarTable";
import CellResourceReporting from "../table/staffingcalendar/CellResourceReporting";
import HeaderResourceCalendar from "../table/staffingcalendar/HeaderResourceCalendar";
import {dateUtils} from "../../../utils/dateUtils";
import Icon from "../../icon/Icon";

export interface StaffingReportingSearchFilter extends SearchFilter {
  year: string,
  resourceIds: string[],
  activityAreas: string[],
  resourceTypeIds?: string[],
}

const StaffingReporting: FunctionComponent<RouteProps> = () => {

  const intl = useIntl()
  const [datas, setDatas] = useState<ReportingData[]>([])
  const [page, setPage] = useState<number>(0)
  const [last, setLast] = useState<boolean>(false)
  const [dynamicColumns, setDynamicColumns] = useState<string[]>([])
  const [sortOptions, setSortOptions] = useState<{sortBy: string, descending: boolean}>({
    sortBy: "header.name",
    descending: false
  })
  const ref = useRef<CalendarTableRef>(null)

  const initialState: StaffingReportingSearchFilter = {
    year: dateUtils.today().getFullYear().toString(),
    resourceIds: [],
    activityAreas: [],
  };

  const {
    searchObject,
    onReset,
    updateSearchField: updateSearchFieldFilter
  } = useSearchFilter<StaffingReportingSearchFilter>(initialState)

  const {loading, startLoading, stopLoading} = useLoading();

  const nextPage = () => {
    setPage(page + 1)
    startLoading()
    staffingCalendarService.getReporting({page, pageSize: 20, sortOptions: [sortOptions]}, searchObject)
      .then((data) => {
        setDatas(prev => [...prev, ...data.content.map(row => ({...row.header, ...row.reportingByMonth}))])
        setDynamicColumns(data.headers)
        setLast(data.last)
      }).catch(console.error)
      .finally(stopLoading)
  }

  const fetchDataFirstPage = (filters) => {
    startLoading()
    setPage(0)
    staffingCalendarService.getReporting({page: 0, pageSize: 20, sortOptions: [sortOptions]}, filters)
      .then((data) => {
        setDatas(data.content.map(row => ({...row.header, ...row.reportingByMonth})))
        setDynamicColumns(data.headers)
        setLast(data.last)
        ref?.current?.goTo(0)
      }).catch(console.error)
      .finally(stopLoading)
  }

  const updateSearchField = (field: string, value: boolean | string | string[]) => {
    updateSearchFieldFilter(field, value)
    fetchDataFirstPage({...searchObject, [field]: value})
  }

  const handleSortToggle = () => {
    const currentSortBy = sortOptions.sortBy
    const currentDescending = sortOptions.descending

    let newSortBy = currentSortBy
    let newDescending: boolean

    // Cycle through: Name ASC → Name DESC → Total ASC → Total DESC → Name ASC
    if (currentSortBy === "header.name" && !currentDescending) {
      newDescending = true
    } else if (currentSortBy === "header.name" && currentDescending) {
      newSortBy = "header.currentYearHourCount"
      newDescending = false
    } else if (currentSortBy === "header.currentYearHourCount" && !currentDescending) {
      newDescending = true
    } else {
      // Reset to default
      newSortBy = "header.name"
      newDescending = false
    }

    setSortOptions({sortBy: newSortBy, descending: newDescending})
  }

  const getSortIcon = () => {
    const {sortBy, descending} = sortOptions

    if (sortBy === "header.name" && !descending) {
      return <><Icon name="ArrowUp" className="me-1" /> {intl.formatMessage({id: "user_first_name"})}</>
    } else if (sortBy === "header.name" && descending) {
      return <><Icon name="ArrowDown" className="me-1" /> {intl.formatMessage({id: "user_first_name"})}</>
    } else if (sortBy === "header.currentYearHourCount" && !descending) {
      return <><Icon name="ArrowUp" className="me-1" /> {intl.formatMessage({id: "purchase_booking_total"})}</>
    } else {
      return <><Icon name="ArrowDown" className="me-1" /> {intl.formatMessage({id: "purchase_booking_total"})}</>
    }
  }

  const getCellClassName = (base: string, header: string) => {
    const currentMonthNumber = dateUtils.today().getMonth()
    const currentMonth = MONTHS_NUMBERS[currentMonthNumber];

    return `${base} ${header === currentMonth ? "epow-bg-blue" : ""}`
  }

  const columns = useMemo(() => {
    return [{
      accessor: "resource",
      sticky: "left",
      Header: (
        <div
          className="empty cursor-pointer d-flex align-items-center justify-content-center p-2"
          onClick={handleSortToggle}
          title="Cliquer pour changer le tri"
        >
          <span className="fw-bold">{getSortIcon()}</span>
        </div>
      ),
      width: 300,
      Cell: (cell) => <HeaderResourceCalendar hideAvailability cell={cell} />
    }, ...dynamicColumns.map(header => ({
      Header: <div className={getCellClassName("staffing-calendar__cell p-2 justify-content-center d-flex bold ms-1", header)}><FormattedMessage id={MONTHS_ABV[header]} /></div>,
      accessor: header,
      minWidth: 85,
      maxWidth: 85,
      Cell: (cell) => <CellResourceReporting cell={cell} accessor={header} year={+searchObject.year} className={getCellClassName("cursor-pointer", header)} />
    }))]
  }, [dynamicColumns, sortOptions]);

  useEffect(() => {
    fetchDataFirstPage(searchObject)
  }, [sortOptions])

  return (
    <div className="pt-2">
      <StaffingReportingFilters
        searchObject={searchObject}
        initialState={initialState}
        updateSearchField={updateSearchField}
        onReset={onReset}
        fetchDataFirstPage={fetchDataFirstPage}
      />

      <div className="table_wrapper mt-5">
        <CalendarTable
          ref={ref}
          columns={columns}
          datas={datas}
          className="staffing-calendar"
          hasNextPage={!last}
          itemSize={85}
          isNextPageLoading={loading}
          loadNextPage={nextPage} />
      </div>
    </div>)
}


export default StaffingReporting;
